import {useContext, useEffect, useRef, useState} from "react";
import {AppContext} from "../../lib/AppContext";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {getAgeRangeTypes, getInventoryEvents, getInventoryEventTypes} from "../common/api/InventoryApi";
import {formatDateMask} from "../common/Utils";
import Grid from "@mui/material/Grid";
import {useNavigate, useSearchParams} from "react-router-dom";
import MDTypography from "../../components/MDTypography";
import {Card, CardContent} from "@mui/material";
import FileSelector from "../calendarUI/FilterSelector";
import {CONSTANTS} from "../common/constants";
import moment from "moment";

export default function CalendarPane(props) {

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [context, setContext] = useContext(AppContext);
  const [eventDate, setEventDate] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [inventoryRender, setInventoryRender] = useState(1);
  const [events, setEvents] = useState([]);
  const [eventsFiltered, setEventsFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specialEvents, setSpecialEvents] = useState(props?.specialEvents);

  const [inventoryEventTypes, setInventoryEventTypes] = useState([]);
  const [eventTypeFilter, setEventTypeFilter] = useState([]);
  const [ageRangeTypes, setAgeRangeTypes] = useState([]);
  const [ageRangeTypeFilter, setAgeRangeTypeFilter] = useState([]);

  const [ready, setReady] = useState(false);

  const initDate = () => {
    if (searchParams.get('d')) {
      return searchParams.get('d');
    }
    let d = new Date();
    d.setDate(1);
    return d;
  }

  const initialDate = initDate();

  useEffect(()=> {
    if (initialDate) {
      setReady(true);
    }
  }, [initialDate])

  const filteredEvents = (events) => {
    let _events = [];
    for (let j in events) {
      if (showEvent(events[j])) {
        _events.push(events[j]);
      }
    }
    setEventsFiltered(_events);
  }

  useEffect(() => {
    filteredEvents(events);
  }, [eventTypeFilter, ageRangeTypeFilter]);

  const loadInventoryEventTypes = () => {
    getInventoryEventTypes().then(result => {
      if (result.error) {
        setContext({...context, error: {message: result.message}});
      } else {
        setInventoryEventTypes(result);
      }
    });
  }

  const getAgeRangeTypesFn = () => {
    getAgeRangeTypes().then(result => {
      if (result.error) {
        setContext({...context, error: {message: result.message}});
      } else {
        setAgeRangeTypes(result);
      }
    });
  }

  useEffect(() => {
    loadInventoryEventTypes();
    getAgeRangeTypesFn();
  }, []);

  useEffect(() => {
    setSpecialEvents(props?.specialEvents);
  }, [props?.specialEvents]);

  const dateClick = function (info) {
    navigate(`/sales/create/event?initDate=${info.dateStr}`);
  }

  const [previewEvent, setPreviewEvent] = useState(false);

  const eventClick = function (info) {
    navigate(`/event/view/${info.event?.extendedProps?.guid}`);
  }

  const requestIdRef = useRef(0);

  const getInventoryEventsFn = async (req, client) => {

    const requestId = ++requestIdRef.current;

    setEventsLoading(true);
    let result = await getInventoryEvents(req, inventoryRender);

    if (requestId === requestIdRef.current) {
      let _events = [];

      for (let i = 0; i < result?.events?.length; i++) {
        let event = result.events[i];
        let endDate = new Date(event.eventDate);
        endDate.setTime(endDate.getTime() + (event.eventDuration * 1000 * 60));

        let _e = {
          guid: event.guid,
          title: event.name,
          eventType: event.eventType,
          ageRange: event.ageRange,
          start: formatDateMask(event.eventDate, 'YYYY-MM-DD HH:mm:ss'),
          end: formatDateMask(endDate, 'YYYY-MM-DD'),
          displayTime: formatDateMask(event.eventDate, 'hh:mm a'),
          bookedFull: event.bookedFull,
          specialEvent: event?.eventType?.specialEvent,
          initialAvailable: event?.initialAvailable,
          totalReservations: event?.totalReservations
        }
        if (event.eventType?.backgroundColor) {
          _e.backgroundColor = event.eventType?.backgroundColor
        }
        if (event.eventType?.fontColor) {
          _e.fontColor = event.eventType?.fontColor
        }
        _events.push(_e);
      }

      setEvents(_events);
      setEventsLoading(false);
      setInventoryRender(result?.inventoryRender);
      setLoading(false);
    }
  }

  useEffect(() => {
    setEventsFiltered(events);
  }, [events])

  const showEvent = (_event) => {
    let show = (eventTypeFilter.length === 0 || eventTypeFilter.indexOf(_event?.eventType?.id) > -1);
    show = show && (ageRangeTypeFilter.length === 0 || ageRangeTypeFilter.indexOf(_event?.ageRange?.id) > -1);
    return show;
  };

  const [initialRender, setInitialRender] = useState(true);

  const handleDatesSet = (info) => {
    let startDate = new Date(info.start);
    startDate.setHours(0, 0, 0, 0);
    let endDate = new Date(info.end);
    if (!initialRender) {
      let d = moment(new Date(info.view.currentStart) || new Date()).format('YYYY-MM-DD');
      setSearchParams({d});
    }
    setInitialRender(false);

    let req = {
      start: startDate,
      end: endDate,
      specialEvents: specialEvents
    }
    getInventoryEventsFn(req, null);
  }

  function renderEventContent(eventInfo) {
    let bookedFull = eventInfo?.event?.extendedProps?.bookedFull;
    let bgColor = 'inherit';
    let fontColor = 'inherit';
    if (eventInfo?.event?.extendedProps?.eventType?.backgroundColor) {
      bgColor = eventInfo?.event?.extendedProps?.eventType?.backgroundColor;
    }
    if (eventInfo?.event?.extendedProps?.eventType?.fontColor) {
      fontColor = eventInfo?.event?.extendedProps?.eventType?.fontColor
    }
    return (
      <Card
        style={{
          backgroundColor: bgColor
        }}
        className={"w-100"}>
        <CardContent>
          <MDTypography variant="p" style={{
            color: fontColor
          }} fontWeight="bold">
            {eventInfo.event?.title}
          </MDTypography>
          <br/>
          <MDTypography variant="p" style={{
            color: fontColor
          }} fontWeight="regular">
            {bookedFull && <><span className="fa fa-circle-check"></span>&nbsp;</>}
            {eventInfo.event?.extendedProps?.displayTime}
          </MDTypography>
          <br/>
          <MDTypography variant="p" style={{
            color: fontColor
          }} fontWeight="regular">
            Booked: {eventInfo?.event?.extendedProps?.totalReservations}/{eventInfo?.event?.extendedProps?.initialAvailable}
          </MDTypography>
        </CardContent>
      </Card>
    )
  }

  return (
    <div>
      <h5>Calendar</h5>
      <Grid container spacing={3}>
        <FileSelector
          types={inventoryEventTypes}
          typeFilter={eventTypeFilter}
          filterLabel={'Event Types'}
          setFilter={(filters) => {
            setEventTypeFilter(filters)
          }}
        />

        <FileSelector
          types={ageRangeTypes}
          typeFilter={ageRangeTypeFilter}
          filterLabel={'Ages'}
          setFilter={(filters) => {
            setAgeRangeTypeFilter(filters)
          }}
        />

        <Grid item xs={12} xl={12}>
          {ready && <>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView='dayGridMonth'
              weekends={true}
              height={'auto'}
              initialDate={initialDate}
              contentHeight={'auto'}
              eventContent={renderEventContent}
              datesSet={handleDatesSet}
              events={eventsFiltered}
              headerToolbar={CONSTANTS.CALENDAR_TOOLBAR}
              dateClick={dateClick}
              eventClick={eventClick}
            />
          </>}
        </Grid>
      </Grid>
    </div>
  )
}
